import { useEffect, useState, useCallback } from "react";
import { BarChart3, Download, FileDown } from "lucide-react";
import { api, downloadBlob } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { money, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DatePicker } from "@/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const yearStart = () => new Date().getFullYear() + "-01-01";
const NONE = "__none__";

export default function Reports() {
  const { currentId, current } = useCompany();
  const [tab, setTab] = useState("pl");
  const [basis, setBasis] = useState("accrual");
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(todayISO());
  const [jobFilter, setJobFilter] = useState(NONE);
  const [jobs, setJobs] = useState([]);
  const [pl, setPl] = useState(null);
  const [bs, setBs] = useState(null);
  const [tb, setTb] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [ledgerAcct, setLedgerAcct] = useState("");
  const [ledger, setLedger] = useState(null);

  const jobParam = jobFilter !== NONE ? `&job_id=${jobFilter}` : "";

  const loadAll = useCallback(() => {
    if (!currentId) return;
    api.get(`/reports/pl?start=${start}&end=${end}&basis=${basis}${jobFilter !== NONE ? `&job_id=${jobFilter}` : ""}`).then((r) => setPl(r.data));
    api.get(`/reports/balance-sheet?as_of=${end}`).then((r) => setBs(r.data));
    api.get(`/reports/trial-balance?as_of=${end}`).then((r) => setTb(r.data));
  }, [currentId, start, end, basis, jobFilter]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (currentId) {
      api.get("/accounts?include_inactive=true").then((r) => setAccounts(r.data));
      api.get("/jobs").then((r) => setJobs(r.data));
    }
  }, [currentId]);

  useEffect(() => {
    if (ledgerAcct && currentId)
      api.get(`/reports/ledger?account_id=${ledgerAcct}&start=${start}&end=${end}`).then((r) => setLedger(r.data));
  }, [ledgerAcct, start, end, currentId]);

  const exportPack = async () => {
    try {
      await downloadBlob(`/reports/export/pdf?start=${start}&end=${end}&basis=${basis}${jobParam}`, `accountant-pack-${start}-to-${end}.pdf`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Financial Reports</h1>
          <p className="text-slate-500 text-sm mt-1">{current?.name} · double-entry books</p>
        </div>
        <Button data-testid="export-pack-btn" onClick={exportPack} className="rounded-md bg-slate-900 hover:bg-slate-800">
          <FileDown className="h-4 w-4 mr-2" /> Export Accountant Pack (PDF)
        </Button>
      </div>

      <Card className="p-4 rounded-lg border-slate-200 shadow-sm mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label>{tab === "bs" || tab === "tb" ? "Period start (P&L only)" : "From"}</Label>
            <div className="mt-1">
              <DatePicker testId="report-start-picker" value={start} onChange={setStart} />
            </div>
          </div>
          <div>
            <Label>{tab === "bs" || tab === "tb" ? "As of date" : "To"}</Label>
            <div className="mt-1">
              <DatePicker testId="report-end-picker" value={end} onChange={setEnd} />
            </div>
          </div>
          <div>
            <Label>Accounting basis</Label>
            <div className="mt-1 inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="basis-toggle">
              {["accrual", "cash"].map((b) => (
                <button
                  key={b}
                  data-testid={`basis-${b}`}
                  onClick={() => setBasis(b)}
                  className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                    basis === b ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-4">
          <div className="sm:w-72">
            <Label>Job filter (P&amp;L &amp; export)</Label>
            <Select value={jobFilter} onValueChange={setJobFilter}>
              <SelectTrigger data-testid="report-job-filter" className="mt-1 rounded-md">
                <SelectValue placeholder="All jobs (whole company)" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value={NONE}>All jobs (whole company)</SelectItem>
                {jobs.map((j) => (
                  <SelectItem key={j.id} value={j.id}>{j.code ? `${j.code} · ` : ""}{j.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {jobFilter !== NONE && (
            <p className="text-xs text-slate-400 sm:pb-2">Showing P&amp;L and GST for this job only. Balance Sheet &amp; Trial Balance remain whole-company.</p>
          )}
        </div>
        {(tab === "bs" || tab === "tb") && (
          <p className="text-xs text-slate-400 mt-3">
            Balance Sheet &amp; Trial Balance are always presented on an accrual basis. The basis toggle affects the P&amp;L, GST return and the exported pack.
          </p>
        )}
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="pl" data-testid="tab-pl">Profit &amp; Loss</TabsTrigger>
          <TabsTrigger value="bs" data-testid="tab-bs">Balance Sheet</TabsTrigger>
          <TabsTrigger value="tb" data-testid="tab-tb">Trial Balance</TabsTrigger>
          <TabsTrigger value="gl" data-testid="tab-gl">General Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="pl">
          <Card className="rounded-lg border-slate-200 shadow-sm p-0 overflow-hidden" data-testid="report-pl">
            <Section title="Income">
              {(pl?.income || []).map((r) => <Row key={r.code} {...r} />)}
              <TotalRow label="Total Income" amount={pl?.total_income} tone="text-emerald-700" />
            </Section>
            <Section title="Expenses">
              {(pl?.expense || []).map((r) => <Row key={r.code} {...r} />)}
              <TotalRow label="Total Expenses" amount={pl?.total_expense} tone="text-amber-700" />
            </Section>
            <div className="flex items-center justify-between px-5 py-4 bg-slate-900 text-white">
              <span className="font-heading font-semibold">Net Income</span>
              <span className="font-heading text-xl font-bold tabular-nums" data-testid="pl-net-income">{money(pl?.net_income)}</span>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="bs">
          <Card className="rounded-lg border-slate-200 shadow-sm p-0 overflow-hidden" data-testid="report-bs">
            <Section title="Assets">
              {(bs?.assets || []).map((r) => <Row key={r.code} {...r} />)}
              <TotalRow label="Total Assets" amount={bs?.total_assets} tone="text-blue-700" />
            </Section>
            <Section title="Liabilities">
              {(bs?.liabilities || []).map((r) => <Row key={r.code} {...r} />)}
              <TotalRow label="Total Liabilities" amount={bs?.total_liabilities} tone="text-rose-700" />
            </Section>
            <Section title="Equity">
              {(bs?.equity || []).map((r) => <Row key={r.code} {...r} />)}
              <TotalRow label="Total Equity" amount={bs?.total_equity} tone="text-violet-700" />
            </Section>
            <div className="flex items-center justify-between px-5 py-4 bg-slate-900 text-white">
              <span className="font-heading font-semibold flex items-center gap-2">
                Liabilities + Equity
                <Badge variant="outline" className={bs?.balanced ? "bg-emerald-500/20 text-emerald-300 border-emerald-400" : "bg-rose-500/20 text-rose-300 border-rose-400"}>
                  {bs?.balanced ? "Balanced" : "Unbalanced"}
                </Badge>
              </span>
              <span className="font-heading text-xl font-bold tabular-nums">{money(bs?.liabilities_plus_equity)}</span>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="tb">
          <Card className="rounded-lg border-slate-200 shadow-sm p-0 overflow-hidden" data-testid="report-tb">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                  <th className="px-5 py-3 font-medium">Code</th>
                  <th className="px-2 py-3 font-medium">Account</th>
                  <th className="px-5 py-3 font-medium text-right">Debit</th>
                  <th className="px-5 py-3 font-medium text-right">Credit</th>
                </tr>
              </thead>
              <tbody>
                {(tb?.rows || []).map((r) => (
                  <tr key={r.code} className="border-b border-slate-50">
                    <td className="px-5 py-2 tabular-nums text-slate-500">{r.code}</td>
                    <td className="px-2 py-2 text-slate-800">{r.name}</td>
                    <td className="px-5 py-2 text-right tabular-nums">{r.debit ? money(r.debit) : ""}</td>
                    <td className="px-5 py-2 text-right tabular-nums">{r.credit ? money(r.credit) : ""}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-semibold">
                  <td className="px-5 py-3" colSpan={2}>Totals</td>
                  <td className="px-5 py-3 text-right tabular-nums" data-testid="tb-total-debit">{money(tb?.total_debit)}</td>
                  <td className="px-5 py-3 text-right tabular-nums" data-testid="tb-total-credit">{money(tb?.total_credit)}</td>
                </tr>
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="gl">
          <Card className="p-4 rounded-lg border-slate-200 shadow-sm mb-4">
            <Label>Account</Label>
            <Select value={ledgerAcct} onValueChange={setLedgerAcct}>
              <SelectTrigger data-testid="ledger-account-select" className="mt-1 rounded-md max-w-md">
                <SelectValue placeholder="Select an account to view its ledger" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-80">
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Card>
          {ledger && (
            <Card className="rounded-lg border-slate-200 shadow-sm p-0 overflow-hidden" data-testid="report-gl">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-heading font-semibold text-slate-900">{ledger.account.code} · {ledger.account.name}</h2>
                <span className="text-sm text-slate-500 tabular-nums">Opening {money(ledger.opening)}</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                    <th className="px-5 py-2 font-medium">Date</th>
                    <th className="px-2 py-2 font-medium">Memo</th>
                    <th className="px-3 py-2 font-medium text-right">Debit</th>
                    <th className="px-3 py-2 font-medium text-right">Credit</th>
                    <th className="px-5 py-2 font-medium text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="px-5 py-2 text-slate-500">{r.date}</td>
                      <td className="px-2 py-2 text-slate-700">{r.memo}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.debit ? money(r.debit) : ""}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.credit ? money(r.credit) : ""}</td>
                      <td className="px-5 py-2 text-right tabular-nums font-medium">{money(r.balance)}</td>
                    </tr>
                  ))}
                  {ledger.rows.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">No activity in this period.</td></tr>
                  )}
                  <tr className="bg-slate-50 font-semibold">
                    <td className="px-5 py-3" colSpan={4}>Closing Balance</td>
                    <td className="px-5 py-3 text-right tabular-nums" data-testid="gl-closing">{money(ledger.closing)}</td>
                  </tr>
                </tbody>
              </table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="px-5 py-2 bg-slate-50 border-y border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </div>
      <table className="w-full text-sm"><tbody>{children}</tbody></table>
    </div>
  );
}
function Row({ code, name, amount }) {
  return (
    <tr className="border-b border-slate-50">
      <td className="px-5 py-2 tabular-nums text-slate-400 w-20">{code}</td>
      <td className="px-2 py-2 text-slate-700">{name}</td>
      <td className="px-5 py-2 text-right tabular-nums text-slate-900">{money(amount)}</td>
    </tr>
  );
}
function TotalRow({ label, amount, tone }) {
  return (
    <tr className="border-t border-slate-200">
      <td></td>
      <td className={`px-2 py-2 font-semibold ${tone}`}>{label}</td>
      <td className={`px-5 py-2 text-right tabular-nums font-bold ${tone}`}>{money(amount)}</td>
    </tr>
  );
}
